
import React, { useEffect, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCart } from "./redux/cartSlice";
import { Toaster } from "react-hot-toast";

import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Home";

// Public Pages
const About             = React.lazy(() => import("./pages/About"));
const DriverLicense     = React.lazy(() => import("./pages/DriverLicense"));
const Contact           = React.lazy(() => import("./pages/Contact"));
const Blog              = React.lazy(() => import("./pages/Blog"));
const BlogDetail        = React.lazy(() => import("./pages/BlogDetail"));
const Faq               = React.lazy(() => import("./pages/Faq"));
const Specification     = React.lazy(() => import("./pages/Specifications"));
const Option            = React.lazy(() => import("./pages/Options"));
const JdmTour           = React.lazy(() => import("./pages/JdmTour"));
const PrintOrder        = React.lazy(() => import("./pages/PrintOrder"));

// Tour Pages
const Asakusa           = React.lazy(() => import("./pages/asakusa/Asakusa"));
const Shibuya           = React.lazy(() => import("./pages/shibuya/Shibuya"));
const Shinagawa         = React.lazy(() => import("./pages/shinagawa/Shinagawa"));
const Shinjuku          = React.lazy(() => import("./pages/shinjuku/Shinjuku"));
const Osaka             = React.lazy(() => import("./pages/osaka/Osaka"));
const FujiTour          = React.lazy(() => import("./pages/fujitour/FujiTour"));
const HelicopterRide    = React.lazy(() => import("./pages/helicopter/HelicopterRide"));
const Rainbow         = React.lazy(() => import("./pages/shinagawa/Rainbow"));


const NotFound         = React.lazy(() => import("./pages/NotFound"));
 

// Booking Pages
const BookingAsakusa    = React.lazy(() => import("./pages/asakusa/Booking-asakusa"));
const BookingShibuya    = React.lazy(() => import("./pages/shibuya/Booking-shibuya"));
const BookingShinagawa  = React.lazy(() => import("./pages/shinagawa/Booking-shinagawa"));
const BookingShinjuku   = React.lazy(() => import("./pages/shinjuku/Booking-shinjuku"));
const BookingOsaka      = React.lazy(() => import("./pages/osaka/Booking-osaka"));
const BookingFujitour   = React.lazy(() => import("./pages/fujitour/Booking-fujitour"));
const BookingRainbow   = React.lazy(() => import("./pages/shinagawa/Booking-rainbow"));

// Payment Pages
const StripeSuccess     = React.lazy(() => import("./pages/StripeSuccess"));
const StripeCancel      = React.lazy(() => import("./pages/StripeCancel"));
const PaypalSuccess     = React.lazy(() => import("./pages/PaypalSuccess"));
const PayPalCancel      = React.lazy(() => import("./pages/PayPalCancel"));

// Admin Pages
const Login             = React.lazy(() => import("./admin/pages/Login"));
const AdminLayout       = React.lazy(() => import("./admin/components/AdminLayout"));
const ProtectedRoute    = React.lazy(() => import("./admin/components/ProtectedRoute"));
const Dashboard         = React.lazy(() => import("./admin/pages/Dashboard"));
const Bookings          = React.lazy(() => import("./admin/pages/Bookings/Booking"));
const AddBooking        = React.lazy(() => import("./admin/pages/Bookings/AddBooking"));
const EditBooking       = React.lazy(() => import("./admin/pages/Bookings/EditBooking"));
const ContactList       = React.lazy(() => import("./admin/pages/Contact/ContactList"));
const JdmList           = React.lazy(() => import("./admin/pages/Contact/JdmList"));
const CouponsList       = React.lazy(() => import("./admin/pages/Coupons/CouponsList"));
const AddCoupon         = React.lazy(() => import("./admin/pages/Coupons/AddCoupon"));
const BlogList          = React.lazy(() => import("./admin/pages/Blogs/BlogList"));
const AddBlog           = React.lazy(() => import("./admin/pages/Blogs/AddBlog"));
const EditBlog          = React.lazy(() => import("./admin/pages/Blogs/EditBlog"));
const Backup            = React.lazy(() => import("./admin/pages/Backup/Backup"));
const Vip               = React.lazy(() => import("./admin/pages/Vip/Vip"));
const Inventory         = React.lazy(() => import("./admin/pages/Inventory/Inventory"));
const MtFujiBookings    = React.lazy(() => import("./admin/pages/MtFuji/MtFujiBookings"));

// Loading fallback
const LoadingScreen = () => (
  <div className="h-screen w-full flex items-center justify-center bg-white">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-500"></div>
  </div>
);

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    // Load cart from localStorage on mount
    const items = localStorage.getItem("cartItems");
    if (items) {
      try {
        dispatch(setCart(JSON.parse(items)));
      } catch (error) {
        console.error("Error loading cart:", error);
      }
    }

    // Sync cart across tabs
    const handleStorage = () => {
      const items = localStorage.getItem("cartItems");
      if (items) {
        try {
          dispatch(setCart(JSON.parse(items)));
        } catch (error) {
          console.error("Error loading cart:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [dispatch]);

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingScreen />}>
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route path="/"                         element={<Home />} />
          <Route path="/about"                    element={<About />} />
          <Route path="/faq"                      element={<Faq />} />
          <Route path="/driver-license"           element={<DriverLicense />} />
          <Route path="/contact"                  element={<Contact />} />
          <Route path="/blogs"                    element={<Blog />} />
          <Route path="/blog/:slug"               element={<BlogDetail />} />
          <Route path="/jdm-tour-tokyo"                 element={<JdmTour />} />
          <Route path="/specifications"           element={<Specification />} />
          <Route path="/options"                  element={<Option />} />
          <Route path="/print/:order_no"          element={<PrintOrder />} />


          <Route path="*" element={<NotFound />} /> 

          {/* Tour Routes */}
          <Route path="/asakusa-gokart-route"     element={<Asakusa />} />
          <Route path="/shibuya-gokart-route"     element={<Shibuya />} />
          <Route path="/shinagawa-gokart-route"   element={<Shinagawa />} />
           <Route path="/rainbow-bridge-gokart-route"   element={<Rainbow />} />
          
          <Route path="/shinjuku-gokart-route"    element={<Shinjuku />} />
          <Route path="/osaka-gokart-route"       element={<Osaka />} />
          <Route path="/mt-fuji-tour"             element={<FujiTour />} />
          <Route path="/helicopter-ride"          element={<HelicopterRide />} />

          {/* Booking Routes */}
          <Route path="/booking-asakusa"          element={<BookingAsakusa />} />
          <Route path="/booking-shibuya"          element={<BookingShibuya />} />
          <Route path="/booking-shinagawa"        element={<BookingShinagawa />} />
          <Route path="/booking-shinjuku"         element={<BookingShinjuku />} />
          <Route path="/booking-osaka"            element={<BookingOsaka />} />
          <Route path="/booking-fujitour"         element={<BookingFujitour />} />
          <Route path="/booking-rainbow"         element={<BookingRainbow />} />

          {/* Payment Routes */}
          <Route path="/stripe/success/:order_no" element={<StripeSuccess />} />
          <Route path="/stripe/pending/:order_no" element={<StripeCancel />} />
          <Route path="/paypal/success/:order_no" element={<PaypalSuccess />} />
          <Route path="/paypal/cancel/:order_no"  element={<PayPalCancel />} />

          {/* Admin Auth */}
          <Route path="/admin/login"              element={<Login />} />

          {/* Protected Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index                          element={<Dashboard />} />
            <Route path="dashboard"               element={<Dashboard />} />
            <Route path="bookings"                element={<Bookings />} />
            <Route path="bookings/add"            element={<AddBooking />} />
            <Route path="bookings/edit/:id"       element={<EditBooking />} />
            <Route path="contact"                 element={<ContactList />} />
            <Route path="jdm"                     element={<JdmList />} />
            <Route path="coupons"                 element={<CouponsList />} />
            <Route path="coupons/add"             element={<AddCoupon />} />
            <Route path="blogs"                   element={<BlogList />} />
            <Route path="blogs/add"               element={<AddBlog />} />
            <Route path="blogs/edit/:id"          element={<EditBlog />} />
            <Route path="vip"                     element={<Vip />} />
            <Route path="inventory"               element={<Inventory />} />
            <Route path="mt-fuji-bookings"        element={<MtFujiBookings />} />
            <Route path="backup"                  element={<Backup />} />


            


          </Route>
        </Routes>
      </Suspense>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            maxWidth: "90vw",
          },
        }}
      />
    </BrowserRouter>
  );
}

export default App;
